import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [adapterVerified, setAdapterVerified] = useState(false);
  const [userInitiated, setUserInitiated] = useState(false);
  const [hasAttemptedConnectOnce, setHasAttemptedConnectOnce] = useState(false);

  // Adapter verification and security checks
  const verifyAdapter = useCallback(async (walletObject) => {
    try {
      // 1. Check if it's a valid object with expected properties
      if (!walletObject || typeof walletObject !== 'object') {
        throw new Error('Invalid wallet adapter: not an object');
      }

      // 2. Verify required methods exist and are functions
      const requiredMethods = ['isAvailable', 'connect', 'disconnect'];
      const missingMethods = requiredMethods.filter(method => 
        !walletObject[method] || typeof walletObject[method] !== 'function'
      );
      
      if (missingMethods.length > 0) {
        throw new Error(`Invalid wallet adapter: missing methods ${missingMethods.join(', ')}`);
      }

      // 3. Check for suspicious properties that might indicate tampering
      const suspiciousProps = ['eval', 'Function', 'setTimeout', 'setInterval', 'XMLHttpRequest', 'fetch'];
      const hasSuspiciousProps = suspiciousProps.some(prop => 
        walletObject.hasOwnProperty(prop) || walletObject[prop]
      );
      
      if (hasSuspiciousProps) {
        throw new Error('Invalid wallet adapter: contains suspicious properties');
      }

      // 4. Verify the adapter is from the expected source
      const expectedSource = 'midenWallet';
      if (walletObject !== window[expectedSource]) {
        // Wallet adapter source verification failed
      }

      // 5. Test basic functionality without side effects
      try {
        if (typeof walletObject.isAvailable === 'function') {
          await walletObject.isAvailable();
        }
      } catch (testError) {
        throw new Error(`Wallet adapter test failed: ${testError.message}`);
      }

      // 6. Check adapter version/identity if available
      if (walletObject.version) {
        // Wallet adapter version available
      }

      setAdapterVerified(true);
      return true;
    } catch (error) {
      setAdapterVerified(false);
      return false;
    }
  }, []);

  // Security logging for wallet operations
  // Includes detailed context: userInitiated, adapterVerified, userAgent, url, and clear reason
  const logWalletEvent = useCallback((event, details = {}) => {
    // In production, send to security monitoring
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to security monitoring service
      // const logEntry = {
      //   timestamp: new Date().toISOString(),
      //   event,
      //   details: {
      //     ...details,
      //     userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'N/A',
      //     url: typeof window !== 'undefined' ? window.location.href : 'N/A',
      //     adapterVerified,
      //     userInitiated
      //   }
      // };
      // Example: sendToSecurityMonitoring(logEntry);
    }
  }, []);

  // Check for Miden Wallet extension 
  useEffect(() => {
    const checkWalletAvailability = async () => {
      if (typeof window !== 'undefined') {
        try {
          // Check for the actual Miden Wallet extension
          // Note: We still detect the wallet even in unsupported environments,
          // but connection will be blocked in connect() if environment doesn't support extensions
          if (window.midenWallet && typeof window.midenWallet === 'object') {
            // Check if it has the expected methods
            const hasRequiredMethods = window.midenWallet.isAvailable && 
                                     window.midenWallet.connect && 
                                     window.midenWallet.disconnect;
            
            if (hasRequiredMethods) {
              // Verify the adapter before setting it
              const isVerified = await verifyAdapter(window.midenWallet);
              if (isVerified) {
              setWallet(window.midenWallet);
                logWalletEvent('adapter_verified', { source: 'midenWallet' });
              return true;
              } else {
                return false;
              }
            }
          }
          
          // Fallback: Check for other possible wallet objects
          const walletObjects = [
            window.miden,
            window.midenWalletAdapter,
            window.midenWalletExtension
          ].filter(Boolean);
          
          if (walletObjects.length > 0) {
            const candidate = walletObjects[0];
            
            // Verify the fallback wallet has required methods
            const hasRequiredMethods =
              candidate &&
              typeof candidate.isAvailable === 'function' &&
              typeof candidate.connect === 'function' &&
              typeof candidate.disconnect === 'function';
            
            if (!hasRequiredMethods) {
              setWallet(null);
              setAdapterVerified(false);
              return false;
            }
            
            // Verify the adapter before using it
            const isVerified = await verifyAdapter(candidate);
            if (!isVerified) {
              setWallet(null);
              return false;
            }
            
            setWallet(candidate);
            logWalletEvent('adapter_verified', { source: 'fallback_miden' });
            return true;
          }
          
          setWallet(null);
          setAdapterVerified(false);
          return false;
        } catch (error) {
          setWallet(null);
          return false;
        }
      }
      setWallet(null);
      return false;
    };

    // Check immediately
    checkWalletAvailability();
    
    // Also check after a short delay in case the extension loads asynchronously
    const timeoutId = setTimeout(checkWalletAvailability, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [verifyAdapter, logWalletEvent]);

  /**
   * Connect to Miden Wallet
   * 
   * Error classes:
   * 1. Unsupported environment - Browser doesn't support Chrome extensions (mobile, Safari, Firefox)
   * 2. User explicitly cancelled - User clicked cancel/close in wallet UI
   * 3. Technical failure - Wallet not available, no account returned, internal error
   */
  const connect = useCallback(
    async (userInitiatedParam = true) => {
    // Set userInitiated synchronously BEFORE any async operations or logWalletEvent calls
    setUserInitiated(userInitiatedParam);
    
    // Security: Only allow user-initiated connections
    if (!userInitiatedParam) {
      logWalletEvent('connection_blocked', { reason: 'not_user_initiated' });
      throw new Error('Wallet connections must be user-initiated for security.');
    }

    setConnecting(true);
    setError(null);

    try {
      if (typeof window === 'undefined') {
        const err = new Error('Wallet connection not available in this environment.');
        logWalletEvent('connection_failed', { 
          reason: 'no_window_object',
            error: err.message,
        });
        throw err;
      }

      if (!wallet) {
          const err = new Error(
            'Miden Wallet extension not found. Please install it from the Chrome Web Store.'
          );
        logWalletEvent('connection_failed', { 
          reason: 'wallet_not_found',
            error: err.message,
        });
        throw err;
      }

      if (!adapterVerified) {
          const err = new Error(
            'Wallet adapter not verified. Please refresh the page and try again.'
          );
          logWalletEvent('connection_blocked', {
            reason: 'adapter_not_verified',
            error: err.message,
          });
        throw err;
      }

      logWalletEvent('connection_attempt', { adapterVerified });

      let connectedAccount = null;

        // 1. Check availability first
        if (wallet.isAvailable && typeof wallet.isAvailable === 'function') {
          const isAvailable = await wallet.isAvailable();
          logWalletEvent('wallet_availability_check', { isAvailable });
          if (!isAvailable) {
            const err = new Error(
              'Miden Wallet extension is not available. Please ensure it is installed and unlocked.'
            );
            logWalletEvent('connection_failed', { 
              reason: 'wallet_not_available',
              error: err.message,
            });
            throw err;
          }
        }

        // 2. If already connected, just restore
        if (wallet.address) {
          connectedAccount = wallet.address;
          logWalletEvent('connection_restored', { account: connectedAccount });
        } else {
          // 3. Call connect() – this may first show an unlock popup, then a dApp connect
          logWalletEvent('connection_modal_triggered');
          
          let connectError = null;
          let connectResult = null;
          try {
            connectResult = await wallet.connect(
            'UPON_REQUEST', // privateDataPermission
            'testnet',      // network
            1               // allowedPrivateData (Assets)
          );
          } catch (e) {
            connectError = e;
          }

          // Classify error, if any
          if (connectError) {
            const msg = connectError?.message?.toLowerCase?.() || '';
            const isUserCancellation =
              msg.includes('cancelled') ||
              msg.includes('user rejected') ||
              connectError.code === 'USER_REJECTED';

            // If user explicitly cancelled, treat as real cancel
            if (isUserCancellation) {
              logWalletEvent('connection_cancelled', {
                reason: 'user_cancelled',
                error: connectError.message,
                adapterVerified,
              });
              throw connectError;
            }

            // If this is the FIRST attempt and error looks like the wallet just closed,
            // treat it as an "unlock phase" and let the user try again without a hard error.
            const looksLikeUnlockClose =
              msg.includes('window closed') ||
              msg.includes('popup closed') ||
              msg.includes('cancelled') ||
              msg === '';

            if (!hasAttemptedConnectOnce && looksLikeUnlockClose) {
              setHasAttemptedConnectOnce(true);
              logWalletEvent('connection_unlock_phase', {
                reason: 'first_attempt_popup_closed',
                error: connectError.message,
              });
              // Surface a softer error so UI can show guidance but not treat as rejection
              throw new Error(
                'Wallet was unlocked or popup closed. Click Connect again to finish linking your wallet.'
              );
            }

            // Otherwise treat as real technical error
            logWalletEvent('connection_error', {
              reason: 'connection_error',
              error: connectError.message,
              adapterVerified,
            });
            throw connectError;
          }

          // 4. After connect() resolves, check for address in multiple places
          // First, check the connect() result for address property
          const resultAddress =
            connectResult?.address ||
            connectResult?.account?.address ||
            connectResult?.accountId ||
            connectResult?.account?.id ||
            null;

          // Then poll briefly for wallet.address (in case it's set asynchronously)
          const pollTimeoutMs = 1000;
          const pollIntervalMs = 100;
          const start = Date.now();

          const pollForAddress = async () => {
            while (!wallet.address && Date.now() - start < pollTimeoutMs) {
              await new Promise((r) => setTimeout(r, pollIntervalMs));
            }
            return wallet.address || null;
          };

          // Try all sources: result from connect(), wallet.address, or poll
          connectedAccount =
            resultAddress ||
            wallet.address ||
            (await pollForAddress());

          if (connectedAccount) {
            logWalletEvent('connection_successful', { account: connectedAccount });
          } else {
            const err = new Error(
              'Wallet connected but no account was provided. Please ensure your Miden Wallet has at least one account and is fully set up, then try again.'
            );
            logWalletEvent('connection_failed', { 
              reason: 'no_account_returned',
              error: err.message,
            });
            throw err;
          }
        }
        
        // 5. Only store connection state when we have a valid account
        if (!connectedAccount) {
          const err = new Error(
            'No accounts available. Please ensure your Miden Wallet extension has an account.'
          );
          logWalletEvent('connection_failed', {
            reason: 'no_accounts_available',
            error: err.message,
        });
          throw err;
      }

        setHasAttemptedConnectOnce(true);
        setPublicKey({ toString: () => connectedAccount });
        setConnected(true);
        
        localStorage.setItem('miden_wallet_connected', 'true');
        localStorage.setItem('miden_wallet_account', connectedAccount);
        
        logWalletEvent('wallet_connected', { 
          account: connectedAccount,
          adapterVerified,
          userInitiated: true,
        });

        // Optional: begin signed nonce auth (best-effort, non-blocking)
        try {
          const origin = window.location.origin.replace(/:\d+$/, '');
          const apiBase = `${origin}:5055`;
          const nonceResp = await fetch(`${apiBase}/auth/nonce?address=${encodeURIComponent(connectedAccount)}`, {
            credentials: 'include'
          });
          const { nonce } = await nonceResp.json();
          if (nonce && wallet.signMessage) {
            const message = `MidenHub Login\nNonce: ${nonce}`;
            const signature = await wallet.signMessage(message);
            await fetch(`${apiBase}/auth/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ address: connectedAccount, message, signature })
            });
          }
        } catch (authErr) {
          // Signed nonce auth skipped/failed
        }

    } catch (err) {
      const errorMessage = err.message || 'Failed to connect to wallet. Please try again.';
      setError(errorMessage);

        const msg = err.message?.toLowerCase?.() || '';
      const isUserCancellation = 
          msg.includes('cancelled') || msg.includes('user rejected') || err.code === 'USER_REJECTED';

      const reason = isUserCancellation ? 'user_cancelled' : 'connection_failed';
      const eventType = isUserCancellation ? 'connection_cancelled' : 'connection_failed';

      logWalletEvent(eventType, {
        reason,
        error: errorMessage,
        adapterVerified,
        userInitiated: true,
      });

      // Re-throw so modal can handle the error with appropriate UX
      throw err;
    } finally {
      setConnecting(false);
    }
    },
    [wallet, adapterVerified, logWalletEvent, hasAttemptedConnectOnce]
  );

  const disconnect = useCallback(async () => {
    setDisconnecting(true);
    setError(null);
    logWalletEvent('disconnect_attempt', { adapterVerified });

    try {
      if (wallet && wallet.disconnect && typeof wallet.disconnect === 'function') {
        await wallet.disconnect();
        logWalletEvent('wallet_disconnected', { adapterVerified });
      } else {
        logWalletEvent('disconnect_fallback', { reason: 'no_disconnect_method' });
      }
      
      setConnected(false);
      setPublicKey(null);
      setUserInitiated(false);
      
      // Clear stored connection state
      localStorage.removeItem('miden_wallet_connected');
      localStorage.removeItem('miden_wallet_account');
    } catch (err) {
      setError(err.message);
      logWalletEvent('disconnect_error', { 
        error: err.message,
        adapterVerified 
      });
    } finally {
      setDisconnecting(false);
    }
  }, [wallet, adapterVerified, logWalletEvent]);

  // Check for existing connection on mount
  useEffect(() => {
    const checkExistingConnection = async () => {
      const wasConnected = localStorage.getItem('miden_wallet_connected') === 'true';
      const storedAccount = localStorage.getItem('miden_wallet_account');
      
      if (wasConnected && storedAccount && wallet) {
        try {
          // Check if the wallet is still available
          if (wallet.isAvailable && typeof wallet.isAvailable === 'function') {
            const isAvailable = await wallet.isAvailable();
            if (!isAvailable) {
              localStorage.removeItem('miden_wallet_connected');
              localStorage.removeItem('miden_wallet_account');
              return;
            }
          }
          
          // Check if the wallet has the stored account
          if (wallet.address === storedAccount) {
            setPublicKey({ toString: () => storedAccount });
            setConnected(true);
          } else {
            // Account no longer matches, clear stored state
            localStorage.removeItem('miden_wallet_connected');
            localStorage.removeItem('miden_wallet_account');
          }
        } catch (error) {
          // Clear stored state on error
          localStorage.removeItem('miden_wallet_connected');
          localStorage.removeItem('miden_wallet_account');
        }
      }
    };

    if (typeof window !== 'undefined' && wallet) {
      checkExistingConnection();
    }
  }, [wallet, logWalletEvent, verifyAdapter]);

  // Transaction modal state (simple inline implementation)
  const [txRequest, setTxRequest] = useState(null);

  const requestSignature = async (tx) => {
    if (!connected || !wallet) {
      throw new Error('Connect wallet first');
    }
    if (!wallet.signTransaction) {
      throw new Error('Wallet does not support signTransaction');
    }
    return new Promise((resolve, reject) => {
      setTxRequest({
        tx,
        resolve,
        reject
      });
    });
  };

  const confirmSign = async () => {
    try {
      const { tx, resolve } = txRequest || {};
      setTxRequest(null);
      const signature = await wallet.signTransaction(tx);
      resolve(signature);
    } catch (e) {
      (txRequest?.reject || (()=>{}))(e);
      setTxRequest(null);
    }
  };

  const cancelSign = () => {
    (txRequest?.reject || (()=>{}))(new Error('User cancelled'));
    setTxRequest(null);
  };

  const value = {
    connected,
    publicKey,
    connecting,
    disconnecting,
    error,
    connect,
    disconnect,
    wallet,
    requestSignature,
    adapterVerified,
    userInitiated
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
      {txRequest && (
        React.createElement(require('../components/WalletTxModal').default, {
          visible: true,
          tx: txRequest.tx,
          onConfirm: confirmSign,
          onCancel: cancelSign
        })
      )}
    </WalletContext.Provider>
  );
};
